import { useState, useMemo, useEffect } from "react"
import { Select } from "antd"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { useUpdateModelNumbers } from "hooks/api/objectsCategories/objectsCategories.hook"
import { useObjectCategoryDrawerStore } from "store/configuration.store"
import { useMessage } from "hooks/message.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { useWithoutCategoryModelNumbers } from "hooks/withoutCategoryModelNumbers.hook"
import { convertApiDimensionsToIDimension } from "helpers/dimensionConversion.helper"
import style from "./LinkModelNumberModal.module.scss"

const { selectWrapper, buttons } = style

type Props = {
	isModalOpen: boolean
	onCancel: () => void
}

export const LinkModelNumberModal = ({ isModalOpen, onCancel }: Props) => {
	const { objectCategoryStore, setObjectCategory, modelNumberToId, setModelNumberToId } = useObjectCategoryDrawerStore()
	const { success } = useMessage()
	const { formatError } = useApiErrorFormatter()
	const { availableModelNumbers } = useWithoutCategoryModelNumbers()
	const { mutate: updateModelNumbers, isPending } = useUpdateModelNumbers()

	const [selectedIds, setSelectedIds] = useState<number[]>([])

	useEffect(() => {
		if (!isModalOpen) {
			setSelectedIds([])
		}
	}, [isModalOpen])

	const options = useMemo(
		() =>
			availableModelNumbers.map((mn) => ({
				value: mn.id,
				label: mn.model_number,
			})),
		[availableModelNumbers],
	)

	const onSaveHandle = () => {
		if (!objectCategoryStore.id || selectedIds.length === 0) return

		updateModelNumbers(
			{ categoryId: objectCategoryStore.id, modelNumberIds: selectedIds },
			{
				onSuccess: () => {
					const updatedDimensions = { ...objectCategoryStore.dimensions }
					const updatedModelNumberToId = { ...modelNumberToId }

					selectedIds.forEach((id) => {
						const mn = availableModelNumbers.find((m) => m.id === id)
						if (mn) {
							updatedDimensions[mn.model_number] = convertApiDimensionsToIDimension(mn.dimensions)
							updatedModelNumberToId[mn.model_number] = mn.id
						}
					})

					setObjectCategory({ ...objectCategoryStore, dimensions: updatedDimensions })
					setModelNumberToId(updatedModelNumberToId)

					success("Model numbers linked successfully")
					onCancel()
				},
				onError: (e) => {
					formatError(e)
				},
			},
		)
	}

	return (
		<Modal title="Link model number" isModalOpen={isModalOpen} setModalClose={onCancel} width={456}>
			<div className={selectWrapper}>
				<span>Model numbers</span>
				<Select
					mode="multiple"
					showSearch
					placeholder="Search model numbers"
					style={{ width: "100%" }}
					value={selectedIds}
					onChange={setSelectedIds}
					options={options}
					optionFilterProp="label"
				/>
			</div>
			<div className={buttons}>
				<ButtonSecondary onClick={onCancel}>Cancel</ButtonSecondary>
				<ButtonPrimary onClick={onSaveHandle} submittable={selectedIds.length > 0 && !isPending}>
					Save
				</ButtonPrimary>
			</div>
		</Modal>
	)
}
